/**
 * A barra do topo do calendário: a marca à esquerda e a escolha do mês à direita.
 *
 * Papel: desenha o monograma e o logotipo Villa Prado separados por um filete,
 * e ao lado deles os controles do mês mostrado — "‹" e "›" para o mês vizinho,
 * a escolha do mês, o campo do ano, "Hoje" e a escolha do primeiro dia da
 * semana. A barra inteira some na impressão.
 *
 * Como usar: a barra não guarda o mês; quem a monta passa `mostrado` e recebe
 * cada troca por `onMudar`. O campo do ano aceita de 2000 a 2100 e só troca o
 * mês mostrado quando o ano digitado cabe nesse intervalo: enquanto a pessoa
 * digita, o valor pela metade (`202`) fica no campo sem mexer na grade, e ao
 * sair do campo ele volta ao ano que a grade está mostrando.
 *
 * As imagens da marca vêm de `public/marca/`, as mesmas da folha impressa.
 */
import { useState } from 'react';

import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { MESES, type MesMostrado, type PrimeiroDia, mesVizinho } from '../grade';

/** O intervalo de anos que o campo aceita. */
const PRIMEIRO_ANO = 2000;
const ULTIMO_ANO = 2100;

interface BarraDoMesProps {
  mostrado: MesMostrado;
  /** Chamado com o mês a mostrar, a cada troca feita na barra. */
  onMudar: (mes: MesMostrado) => void;
  /** Chamado pelo botão "Hoje", para voltar ao mês corrente. */
  onHoje: () => void;
  /** O dia em que a semana da grade começa. */
  primeiroDia: PrimeiroDia;
  /** Chamado com o dia escolhido em "Domingo primeiro / Segunda primeiro". */
  onTrocarPrimeiroDia: (dia: PrimeiroDia) => void;
}

/**
 * A barra do mês.
 *
 * @param mostrado - O mês que a grade está mostrando.
 * @param onMudar - Recebe o mês de destino de "‹", "›", da escolha do mês e do
 *   campo do ano.
 * @param onHoje - Chamado por "Hoje"; é quem monta a barra que sabe qual é o
 *   mês corrente.
 * @param primeiroDia - O dia em que a semana começa, 0 para domingo e 1 para
 *   segunda.
 * @param onTrocarPrimeiroDia - Recebe a troca do primeiro dia da semana.
 * @returns A faixa com a marca e os controles, sobre o filete que a separa da
 *   grade.
 */
export function BarraDoMes({
  mostrado,
  onMudar,
  onHoje,
  primeiroDia,
  onTrocarPrimeiroDia,
}: BarraDoMesProps) {
  // O texto digitado vale só para o ano em que foi digitado: quando "‹" ou "›"
  // viram o ano, o campo volta a mostrar o ano da grade.
  const [digitado, setDigitado] = useState<{ texto: string; ano: number } | null>(null);
  const textoDoAno =
    digitado !== null && digitado.ano === mostrado.ano ? digitado.texto : String(mostrado.ano);

  /** Guarda o que foi digitado e troca o ano quando ele já é um ano aceito. */
  function handleDigitarAno(texto: string) {
    const ano = Number(texto);
    const aceito = /^\d{4}$/.test(texto) && ano >= PRIMEIRO_ANO && ano <= ULTIMO_ANO;
    setDigitado({ texto, ano: aceito ? ano : mostrado.ano });
    if (aceito && ano !== mostrado.ano) onMudar({ ano, mes: mostrado.mes });
  }

  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-4 border-b border-linha-forte pb-4 print:hidden">
      <div className="flex shrink-0 items-center gap-3.5">
        <img
          src="/marca/villa-prado-monograma.png"
          alt="Villa Prado"
          width={26}
          height={26}
          className="block size-[26px] shrink-0 object-contain"
        />
        <span aria-hidden="true" className="h-5 w-px shrink-0 bg-linha-forte" />
        <img
          src="/marca/villa-prado-logotipo.png"
          alt="Villa Prado"
          className="block h-[26px] w-auto object-contain"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          aria-label="Mês anterior"
          className="px-3 py-1.5"
          onClick={() => onMudar(mesVizinho(mostrado, -1))}
        >
          ‹
        </Button>
        <Select
          value={String(mostrado.mes)}
          onValueChange={(valor) => onMudar({ ano: mostrado.ano, mes: Number(valor) })}
        >
          <SelectTrigger
            aria-label="Mês"
            className="min-w-28 bg-superficie px-2.5 py-2 text-[13px] leading-[18px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MESES.map((nome, indice) => (
              <SelectItem key={nome} value={String(indice + 1)}>
                {nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          aria-label="Mês seguinte"
          className="px-3 py-1.5"
          onClick={() => onMudar(mesVizinho(mostrado, 1))}
        >
          ›
        </Button>
        <Input
          type="number"
          aria-label="Ano"
          min={PRIMEIRO_ANO}
          max={ULTIMO_ANO}
          value={textoDoAno}
          onChange={(evento) => handleDigitarAno(evento.target.value)}
          onBlur={() => setDigitado(null)}
          className="w-20 bg-superficie px-2.5 py-2 text-[13px] leading-4"
        />
        <Button type="button" variant="outline" className="px-3 py-1.5" onClick={onHoje}>
          Hoje
        </Button>
        <Select
          value={String(primeiroDia)}
          onValueChange={(valor) => onTrocarPrimeiroDia(valor === '0' ? 0 : 1)}
        >
          <SelectTrigger
            aria-label="Primeiro dia da semana"
            className="min-w-36 bg-superficie px-2.5 py-2 text-[13px] leading-[18px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Domingo primeiro</SelectItem>
            <SelectItem value="1">Segunda primeiro</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
